/**
 * Controller paths on conversation-analytics-api, relative to the configured API base URL --
 * `runtimeConfig.public.baseUrl`, which axios joins in plugins/httpRequestServer.ts. That
 * value comes from NUXT_PUBLIC_BASE_URL in the Crucible ConfigMaps and from
 * VUE_APP_API_PREFIX in the legacy `.env.*` builds.
 *
 * DELIBERATELY WITHOUT AN `/api` PREFIX. The API serves these under `/api/<Controller>`, but
 * which prefix reaches it is the environment's decision, not this file's:
 *
 *   * through the Crucible gateway the public contract is `/ca/v1/cai/<Controller>` on the
 *     shared `api.<env>.maxcontact.com` hostname. Every service gets its own prefix under
 *     `/ca/v1` -- `cai` is conversation-analytics-api's -- and the HTTPRoute rewrites
 *     `/ca/v1/cai` to `/api` before the request reaches the pod
 *     (.crucible/overlays/dev/api-httproute.yaml);
 *   * against a legacy App Service, or a local port-forward, there is no gateway to rewrite
 *     anything, so the base URL carries `/api` itself.
 *
 * Baking `/api` in here would force one of those to carry the prefix twice.
 */
export enum API_ROUTES {
  ANALYTICS = "/Analytics",
  ANALYTICS_JOB = "/AnalyticsJob",
  AUTHENTICATION = "/authentication",
  // Two names for one controller, both in use. `components/scoring/**` reaches it through
  // SCORING_SCHEDULE and `components/autoQA/**` through AUTO_QA_SCHEDULE -- the two page
  // trees are a rename in progress (`/scoring/schedules` and `/auto-qa/schedules` are both
  // routable). AUTO_QA_SCHEDULE was referenced by services/AutoQAScheduleApi.ts without ever
  // being declared, so every call it made resolved to the string "undefined/upsert" and
  // 404ed. Declared rather than deleted because the callers are live; drop it, and the alias
  // with it, when the autoQA tree goes.
  SCORING_SCHEDULE = "/AutoQASchedule",
  AUTO_QA_SCHEDULE = "/AutoQASchedule",
  CONVERSATION_DETAILS = "/ConversationDetails",
  CONVERSATION_PLAYBACK = "/ConversationPlayback",
  FILTERS = "/filters",
  PERFORMANCE_DETAILS = "/PerformanceDetails",
  QUICK_FILTERS = "/QuickFilter",
  SCORECARD = "/Scorecard",
  SEARCH = "/Search",
  SHARED_LINK = "/SharedLink",
  SUCCESS_INTELLIGENCE_METRICS = "/SuccessIntelligenceMetrics",
  USER_PROFILE = "/UserProfile",
};
